import api from './api';

const settingsService = {
  // Alle Einstellungen abrufen (incl. TAPI)
  getSettings: async () => {
    try {
      const response = await api.get('/integrations');
      return response.data;
    } catch (error) {
      console.error('Failed to load settings:', error);
      return null;
    }
  },

  // TAPI-Einstellungen abrufen
  getTapiSettings: async () => {
    try {
      const response = await api.get('/integrations/tapi');
      return response.data;
    } catch (error) {
      console.error('Failed to load TAPI settings:', error);
      return null;
    }
  },

  // TAPI-Einstellungen speichern
  saveTapiSettings: async (tapiSettings) => {
    const response = await api.put('/integrations/tapi', tapiSettings);
    return response.data;
  },

  // ======== Project Types ========
  getProjectTypes: async () => {
    const response = await api.get('/project-types');
    return response.data;
  },

  addProjectType: async (projectType) => {
    const response = await api.post('/project-types', { project_type: projectType });
    return response.data;
  },

  deleteProjectType: async (projectType) => {
    const response = await api.delete(`/project-types/${encodeURIComponent(projectType)}`);
    return response.data;
  },

  // ======== Project Type Tab Config ========
  getTabConfigs: async () => {
    const response = await api.get('/project-types/tabs/all');
    return response.data;
  },

  getTabConfig: async (projectType) => {
    const response = await api.get(`/project-types/tabs/${encodeURIComponent(projectType)}`);
    return response.data;
  },

  updateTabConfig: async (projectType, tabs) => {
    const response = await api.put(`/project-types/tabs/${encodeURIComponent(projectType)}`, { tabs });
    return response.data;
  },

  // ======== Time Units ========
  getTimeUnits: async () => {
    const response = await api.get('/time-units');
    return response.data;
  },

  addTimeUnit: async (value, label) => {
    const response = await api.post('/time-units', { value, label });
    return response.data;
  },

  updateTimeUnit: async (unitValue, newLabel) => {
    const response = await api.put(`/time-units/${encodeURIComponent(unitValue)}`, { label: newLabel });
    return response.data;
  },

  deleteTimeUnit: async (unitValue) => {
    const response = await api.delete(`/time-units/${encodeURIComponent(unitValue)}`);
    return response.data;
  },

  // ======== Address Categories ========
  getAddressCategories: async () => {
    const response = await api.get('/address-categories');
    return response.data;
  },

  addAddressCategory: async (name, color) => {
    const response = await api.post('/address-categories', { name, color });
    return response.data;
  },

  updateAddressCategory: async (categoryId, data) => {
    const response = await api.put(`/address-categories/${categoryId}`, data);
    return response.data;
  },

  deleteAddressCategory: async (categoryId) => {
    const response = await api.delete(`/address-categories/${categoryId}`);
    return response.data;
  },

  // ======== Address Statuses (Adressstatus) ========
  getAddressStatuses: async () => {
    const response = await api.get('/address-statuses');
    return response.data;
  },
  addAddressStatus: async (name, color) => {
    const response = await api.post('/address-statuses', { name, color });
    return response.data;
  },
  updateAddressStatus: async (id, data) => {
    const response = await api.put(`/address-statuses/${id}`, data);
    return response.data;
  },
  deleteAddressStatus: async (id) => {
    const response = await api.delete(`/address-statuses/${id}`);
    return response.data;
  },
  reorderAddressStatus: async (id, sortOrder) => {
    const response = await api.put(`/address-statuses/${id}/reorder`, { sort_order: sortOrder });
    return response.data;
  },

  // ======== Realestate Types (Immobilienarten) ========
  getRealestateTypes: async () => {
    const response = await api.get('/realestate-types');
    return response.data;
  },
  addRealestateType: async (name, color) => {
    const response = await api.post('/realestate-types', { name, color });
    return response.data;
  },
  updateRealestateType: async (id, data) => {
    const response = await api.put(`/realestate-types/${id}`, data);
    return response.data;
  },
  deleteRealestateType: async (id) => {
    const response = await api.delete(`/realestate-types/${id}`);
    return response.data;
  },
  reorderRealestateType: async (id, sortOrder) => {
    const response = await api.put(`/realestate-types/${id}/reorder`, { sort_order: sortOrder });
    return response.data;
  },

  // ======== Project Statuses (Projektstatus) ========
  getProjectStatuses: async () => {
    const response = await api.get('/project-statuses');
    return response.data;
  },
  addProjectStatus: async (name, color) => {
    const response = await api.post('/project-statuses', { name, color });
    return response.data;
  },
  updateProjectStatus: async (id, data) => {
    const response = await api.put(`/project-statuses/${id}`, data);
    return response.data;
  },
  deleteProjectStatus: async (id) => {
    const response = await api.delete(`/project-statuses/${id}`);
    return response.data;
  },
  reorderProjectStatus: async (id, sortOrder) => {
    const response = await api.put(`/project-statuses/${id}/reorder`, { sort_order: sortOrder });
    return response.data;
  },

  // ======== Industries (Branchen) ========
  getIndustries: async () => {
    const response = await api.get('/industries');
    return response.data;
  },

  addIndustry: async (name) => {
    const response = await api.post('/industries', { name });
    return response.data;
  },

  updateIndustry: async (industryId, name) => {
    const response = await api.put(`/industries/${industryId}`, { name });
    return response.data;
  },

  deleteIndustry: async (industryId) => {
    const response = await api.delete(`/industries/${industryId}`);
    return response.data;
  },

  // ======== Salutations (Anreden) ========
  setDefaultSalutation: async (salutationId) => {
    const response = await api.post(`/salutations/${salutationId}/set-default`);
    return response.data;
  },
  clearDefaultSalutation: async () => {
    const response = await api.post('/salutations/clear-default');
    return response.data;
  },

  getSalutations: async () => {
    const response = await api.get('/salutations');
    return response.data;
  },

  addSalutation: async (name, letterPrefix) => {
    const response = await api.post('/salutations', { name, letter_prefix: letterPrefix || null });
    return response.data;
  },

  updateSalutation: async (salutationId, name, letterPrefix) => {
    const response = await api.put(`/salutations/${salutationId}`, { name, letter_prefix: letterPrefix || null });
    return response.data;
  },

  deleteSalutation: async (salutationId) => {
    const response = await api.delete(`/salutations/${salutationId}`);
    return response.data;
  },

  // ======== Update Project Type ========
  updateProjectType: async (oldType, newType) => {
    const response = await api.put(`/project-types/${encodeURIComponent(oldType)}`, { project_type: newType });
    return response.data;
  },

  // ======== Dropdown Values (Business Divisions, etc.) ========
  getDropdownValues: async (category) => {
    const response = await api.get(`/settings/dropdown-values/${category}`);
    return response.data;
  },

  getAllDropdownValues: async () => {
    const response = await api.get('/settings/dropdown-values');
    return response.data;
  },

  addDropdownValue: async (category, value, label, color = null) => {
    const response = await api.post('/settings/dropdown-values', { category, value, label, color });
    return response.data;
  },

  updateDropdownValue: async (valueId, category, value, label, color = null) => {
    const response = await api.put(`/settings/dropdown-values/${valueId}`, { category, value, label, color });
    return response.data;
  },

  deleteDropdownValue: async (valueId) => {
    const response = await api.delete(`/settings/dropdown-values/${valueId}`);
    return response.data;
  },

  toggleDropdownBadge: async (valueId, showInBadges) => {
    const response = await api.patch(`/settings/dropdown-values/${valueId}/badge`, { show_in_badges: showInBadges });
    return response.data;
  },

  // Business Divisions (Unternehmensbereiche) - Convenience Methods
  getBusinessDivisions: async () => {
    const response = await api.get('/settings/dropdown-values/business_division');
    return response.data.values || [];
  },

  addBusinessDivision: async (name, color = null) => {
    return settingsService.addDropdownValue('business_division', name, name, color);
  },

  deleteBusinessDivision: async (valueId) => {
    return settingsService.deleteDropdownValue(valueId);
  },

  // SEPA-Felder am Unternehmensbereich (Pflichtenheft SEPA-Lastschrift-Workflow, Abschnitt 3.1)
  updateBusinessDivisionSepaFields: async (valueId, data) => {
    const response = await api.put(`/settings/business-divisions/${valueId}/sepa`, data);
    return response.data;
  },

  // Participation Types (Beteiligungsarten)
  initParticipationTypes: async () => {
    const response = await api.post('/settings/dropdown-values/init-participation-types');
    return response.data;
  },

  // ======== Reorder (Drag & Drop) ========
  reorderItems: async (collectionType, orderedIds, category = null) => {
    const response = await api.put('/settings/reorder', {
      collection_type: collectionType,
      ordered_ids: orderedIds,
      category
    });
    return response.data;
  },

  // ======== Lead Pipeline Phasen ========
  getLeadPhases: async () => {
    const response = await api.get('/settings/lead-phases');
    return response.data;
  },

  updateLeadPhases: async (phases) => {
    const response = await api.put('/settings/lead-phases', { phases });
    return response.data;
  },

  // ======== Relationship Types (Beziehungstypen) ========
  getRelationshipTypes: async () => {
    const response = await api.get('/relationship-types');
    return response.data;
  },
  addRelationshipType: async (name, color) => {
    const response = await api.post('/relationship-types', { name, color });
    return response.data;
  },
  updateRelationshipType: async (id, data) => {
    const response = await api.put(`/relationship-types/${id}`, data);
    return response.data;
  },
  deleteRelationshipType: async (id) => {
    const response = await api.delete(`/relationship-types/${id}`);
    return response.data;
  },
  reorderRelationshipType: async (id, sortOrder) => {
    const response = await api.put(`/relationship-types/${id}/reorder`, { sort_order: sortOrder });
    return response.data;
  },
};

export default settingsService;
